/** biome-ignore-all lint/performance/noImgElement: signed posters from ad storage, outside Next's image loader */
"use client";

import { ImageOff, Play, RotateCcw, VideoOff } from "lucide-react";
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { CONTROL_PRESS } from "@/components/ui/design-system/surface-styles";
import { Strong } from "@/components/ui/design-system/text";
import { fillsNineBySixteen } from "@/components/ui/design-system/video-player";
import { cn } from "@/lib/utils";
import type { VideoFeedLabels } from "./labels";
import { isWarm, progressFraction } from "./utils";

/** Clear of the home indicator on a phone; zero everywhere else. */
const BOTTOM_INSET: CSSProperties = {
  paddingBottom: "calc(env(safe-area-inset-bottom) + var(--spacing-nf-ds-6))",
};

/** HTMLMediaElement.HAVE_CURRENT_DATA: there is a frame to show. */
const HAS_FRAME = 2;

/**
 * One video in the feed, a screen tall.
 *
 * On a phone the video is the screen, fitted whole: a 9:16 ad on a taller
 * phone keeps a band above and below rather than losing its sides, captions
 * included. Past rail width it is a 9:16 column in the middle of the dark
 * backdrop, and a file that really is 9:16 fills it.
 *
 * The video element is not this slide's. It is one of the feed's three
 * pooled elements (see `useVideoFeed`), moved into the host here while the
 * slide is current or beside it, and handed on when it is not. Until it has
 * a frame of this slide's file the host stays clear and the poster under it
 * shows; a slide further away is only its poster and loads nothing.
 *
 * A still takes no element at all. It is drawn as its own image, fitted the
 * way a video would be, with nothing to pause and no progress to show.
 */
export function FeedSlide({
  index,
  total,
  src,
  kind,
  poster,
  label,
  labels,
  active,
  current,
  element,
  paused,
  onTogglePlay,
  onRetry,
  caption,
}: {
  index: number;
  /** The set's size for assistive tech, or -1 while more can load. */
  total: number;
  src: string | null;
  kind: "video" | "image";
  poster: string | null;
  label: string;
  labels: VideoFeedLabels;
  /** Whether this is the current video and the feed is open. */
  active: boolean;
  /** The current stop's index, for deciding whether this slide holds a video. */
  current: number;
  /** The pooled element this slide plays on while it is warm. */
  element: HTMLVideoElement;
  paused: boolean;
  onTogglePlay: () => void;
  onRetry?: () => void;
  caption?: ReactNode;
}) {
  const isImage = kind === "image";
  const hostRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  // Each keyed to the file, so a fresh signed URL after a retry clears them.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [framedSrc, setFramedSrc] = useState<string | null>(null);
  const failed = src === null || failedSrc === src;
  const [fill, setFill] = useState(false);
  // A still never takes a pooled element: it has nothing to play on one.
  const warm = !isImage && isWarm(index, current);
  const framed = warm && src !== null && framedSrc === src;

  // Takes the pooled element while warm. `appendChild` moves the node rather
  // than copying it, so it keeps what a tap unlocked on it. The file stays on
  // an element nobody else has claimed, so stepping back to a neighbour
  // finds it loaded.
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!warm || !host) return;
    if (element.parentNode !== host) host.appendChild(element);
    if (src === null) {
      if (element.hasAttribute("src")) {
        element.removeAttribute("src");
        element.load();
      }
    } else if (element.getAttribute("src") !== src) {
      element.setAttribute("src", src);
      setFramedSrc(null);
    } else if (element.readyState >= HAS_FRAME) {
      setFramedSrc(src);
    }

    const onError = () => setFailedSrc(src);
    const onFrame = () => setFramedSrc(src);
    const onMetadata = () => {
      const { videoWidth, videoHeight } = element;
      if (videoWidth > 0 && videoHeight > 0) {
        setFill(fillsNineBySixteen(videoWidth / videoHeight));
      }
    };
    element.addEventListener("error", onError);
    element.addEventListener("loadeddata", onFrame);
    element.addEventListener("loadedmetadata", onMetadata);
    return () => {
      element.removeEventListener("error", onError);
      element.removeEventListener("loadeddata", onFrame);
      element.removeEventListener("loadedmetadata", onMetadata);
    };
  }, [element, src, warm]);

  // The element's look is the slide's to set while the slide holds it.
  useLayoutEffect(() => {
    if (!warm) return;
    element.className = cn(
      "absolute inset-0 size-full object-contain",
      fill && "lg:object-cover",
    );
  }, [element, fill, warm]);

  // The bar is written straight to the element each frame rather than
  // through state, which would re-render the slide sixty times a second.
  // `failed` is a dependency because the bar is not drawn while it holds.
  useEffect(() => {
    const bar = barRef.current;
    if (!active || failed || isImage || !bar) return;
    let frame = 0;
    const tick = () => {
      bar.style.transform = `scaleX(${progressFraction(element.currentTime, element.duration)})`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, element, failed, isImage]);

  const retry = () => {
    setFailedSrc(null);
    if (warm && src) {
      element.load();
      if (active) element.play().catch(() => {});
    }
    // An expired link fails the same way again however often it is
    // reloaded; the caller refetches, and the new link clears the failure.
    onRetry?.();
  };

  return (
    <article
      // `STOP_ATTRIBUTE`, which the feed observes and scrolls to.
      data-feed-stop={index}
      aria-label={label}
      aria-posinset={index + 1}
      aria-setsize={total}
      className="flex h-full w-full snap-start snap-always items-center justify-center lg:py-nf-ds-6"
    >
      <div className="relative h-full w-full overflow-hidden bg-nf-ds-black lg:aspect-9/16 lg:w-auto lg:max-w-full lg:rounded-nf-ds-card">
        {isImage && src ? (
          <img
            src={src}
            alt=""
            aria-hidden
            loading="lazy"
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth > 0 && naturalHeight > 0) {
                setFill(fillsNineBySixteen(naturalWidth / naturalHeight));
              }
            }}
            onError={() => setFailedSrc(src)}
            className={cn(
              "absolute inset-0 size-full object-contain",
              fill && "lg:object-cover",
            )}
          />
        ) : poster ? (
          <img
            src={poster}
            alt=""
            aria-hidden
            className={cn(
              "absolute inset-0 size-full object-contain",
              fill && "lg:object-cover",
            )}
          />
        ) : null}
        <div
          ref={hostRef}
          aria-hidden
          className={cn(
            "absolute inset-0",
            framed && !failed ? "opacity-100" : "opacity-0",
          )}
        />

        {failed ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-nf-ds-4 px-nf-ds-8 text-center">
            {isImage ? (
              <ImageOff
                aria-hidden
                className="size-nf-ds-8 text-nf-ds-white/60"
                strokeWidth={1.5}
              />
            ) : (
              <VideoOff
                aria-hidden
                className="size-nf-ds-8 text-nf-ds-white/60"
                strokeWidth={1.5}
              />
            )}
            <Strong className="text-nf-ds-white">{labels.failed}</Strong>
            <button
              type="button"
              onClick={retry}
              className={cn(
                CONTROL_PRESS,
                "inline-flex h-nf-ds-control items-center gap-nf-ds-2 rounded-nf-ds-button bg-nf-ds-white px-nf-ds-5 text-nf-ds-ink",
                "font-nf-ds-sans text-nf-ds-label outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-white focus-visible:ring-offset-2 focus-visible:ring-offset-nf-ds-black",
              )}
            >
              <RotateCcw aria-hidden className="size-nf-ds-4" />
              {labels.retry}
            </button>
          </div>
        ) : isImage ? null : (
          <>
            {/* The whole frame is the pause target: on a phone it is the
                screen, and nothing smaller is as easy to hit. */}
            <button
              type="button"
              onClick={onTogglePlay}
              aria-label={paused ? labels.play : labels.pause}
              className="absolute inset-0 cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-white focus-visible:ring-inset"
            />
            {/* Held while paused, so a still frame reads as stopped rather
                than stuck; it fades the moment the video plays again. */}
            <span
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-0 flex items-center justify-center",
                "transition-opacity duration-200 ease-nf-ds-out motion-reduce:transition-none",
                paused ? "opacity-100" : "opacity-0",
              )}
            >
              <span className="flex size-nf-ds-16 items-center justify-center rounded-nf-ds-pill bg-nf-ds-ink/50 text-nf-ds-white backdrop-blur-sm">
                <Play className="size-nf-ds-8 translate-x-px fill-current" />
              </span>
            </span>
          </>
        )}

        {caption ? (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 bg-linear-to-t from-nf-ds-ink/85 to-transparent px-nf-ds-5 pt-nf-ds-16 text-nf-ds-white"
            style={BOTTOM_INSET}
          >
            {caption}
          </div>
        ) : null}

        {active && !failed && !isImage ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 h-0.5 bg-nf-ds-white/20"
            style={{ bottom: "env(safe-area-inset-bottom)" }}
          >
            {/* The transform is inline, not `scale-x-0`: Tailwind's scale
                is the separate `scale` property, which would multiply the
                frame-by-frame `transform` above to nothing. */}
            <span
              ref={barRef}
              className="block h-full w-full origin-left bg-nf-ds-white"
              style={{ transform: "scaleX(0)" }}
            />
          </span>
        ) : null}
      </div>
    </article>
  );
}
